import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { Search, MessageCircle, Send, User, Bot, Clock } from "lucide-react";
import { format } from "date-fns";

export default function Conversations() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [selectedConv, setSelectedConv] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState("");

  const { data: conversations = [], isLoading } = useQuery({
    queryKey: ["conversations"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("syra_conversations")
        .select("*")
        .eq("type", "direct")
        .order("last_message_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const { data: messages = [] } = useQuery({
    queryKey: ["conv-messages", selectedConv],
    enabled: !!selectedConv,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("syra_messages")
        .select("*")
        .eq("conversation_id", selectedConv!)
        .order("created_at", { ascending: true })
        .limit(200);
      if (error) throw error;
      return data;
    },
  });

  const markRead = useMutation({
    mutationFn: async (convId: string) => {
      await supabase.from("syra_conversations").update({ unread_count: 0 }).eq("id", convId);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["conversations"] }),
  });

  const handleSelectConv = (convId: string) => {
    setSelectedConv(convId);
    markRead.mutate(convId);
  };

  const filtered = conversations.filter((c) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (c.name || "").toLowerCase().includes(s) || (c.contact_jid || "").includes(s);
  });

  const currentConv = conversations.find((c) => c.id === selectedConv);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold font-headline">Conversas 1:1</h1>
        <p className="text-muted-foreground text-sm">Mensagens diretas recebidas pelo WhatsApp</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Conversation list */}
        <div className="lg:col-span-1 space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Buscar contato..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
          </div>

          {isLoading ? (
            <p className="text-sm text-muted-foreground p-4">Carregando...</p>
          ) : filtered.length === 0 ? (
            <Card><CardContent className="p-6 text-center text-muted-foreground text-sm">
              Nenhuma conversa 1:1 ainda. Ative mensagens diretas nas configurações da organização.
            </CardContent></Card>
          ) : (
            <ScrollArea className="h-[550px]">
              <div className="space-y-1.5 pr-2">
                {filtered.map((conv) => (
                  <Card
                    key={conv.id}
                    className={`cursor-pointer transition-colors hover:border-primary/50 ${selectedConv === conv.id ? "border-primary bg-accent/30" : ""}`}
                    onClick={() => handleSelectConv(conv.id)}
                  >
                    <CardContent className="p-3 flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                        <User className="h-4 w-4 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-medium text-sm truncate">{conv.name || conv.contact_jid || "Desconhecido"}</p>
                          {(conv.unread_count ?? 0) > 0 && (
                            <Badge className="text-[10px] shrink-0">{conv.unread_count}</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Clock className="h-3 w-3" />
                          <span>{conv.last_message_at ? format(new Date(conv.last_message_at), "dd/MM HH:mm") : "-"}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </ScrollArea>
          )}
        </div>

        {/* Messages */}
        <div className="lg:col-span-2">
          {currentConv ? (
            <Card className="h-[600px] flex flex-col">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <MessageCircle className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-base">{currentConv.name || "Contato"}</CardTitle>
                    <p className="text-xs text-muted-foreground">{currentConv.contact_jid}</p>
                  </div>
                </div>
              </CardHeader>
              <Separator />
              <ScrollArea className="flex-1 px-4">
                <div className="space-y-3 py-4">
                  {messages.map((msg) => {
                    const isAgent = msg.sender_name === "SYRA" || msg.sender_name === "Syra";
                    return (
                      <div key={msg.id} className={`flex gap-2 ${isAgent ? "justify-end" : ""}`}>
                        <div className={`max-w-[80%] rounded-lg p-3 text-sm ${
                          isAgent ? "bg-primary text-primary-foreground" : "bg-muted"
                        }`}>
                          <div className="flex items-center gap-1.5 mb-1 text-xs opacity-70">
                            {isAgent ? <Bot className="h-3.5 w-3.5" /> : <User className="h-3.5 w-3.5" />}
                            <span>{msg.sender_name || msg.sender_phone}</span>
                            <span>· {format(new Date(msg.created_at), "dd/MM HH:mm")}</span>
                          </div>
                          <p className="whitespace-pre-wrap">{msg.content}</p>
                        </div>
                      </div>
                    );
                  })}
                  {messages.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-8">Nenhuma mensagem ainda</p>
                  )}
                </div>
              </ScrollArea>
              <Separator />
              <div className="p-3">
                <p className="text-xs text-muted-foreground text-center">As respostas são enviadas automaticamente pela IA</p>
              </div>
            </Card>
          ) : (
            <Card className="h-[600px] flex items-center justify-center">
              <div className="text-center space-y-2">
                <MessageCircle className="h-10 w-10 text-muted-foreground/30 mx-auto" />
                <p className="text-muted-foreground text-sm">Selecione uma conversa</p>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
